import { Fragment } from "react";
import useStateWithLocalstorage from "./hooks/useStateWithLocalstorage";
import EditableList from "./components/EditableList";
import EditableText from "./components/EditableText";
import Textarea from "react-textarea-autosize";
import without from "lodash.without"

import { A, Code, P } from "./html";
import { LinkIcon } from "./icons";

const defaultTopics = `forum
mailing list
podcast
reddit
message board
community
group
Twitter
list
Facebook
share
sharing
chat
IRC
blog
best
resources
tutorials
help
FAQs
reviews
questions
wiki
meet-up
meetup
user group
advice
awards
competition
problems
association
customers
client
sales
tools
system
systems`;

function App() {
  const [terms, setTerms] = useStateWithLocalstorage("input1", "");
  const [topics, setTopics] = useStateWithLocalstorage("input2", defaultTopics);
  const [checkedItems, setCheckedItems] = useStateWithLocalstorage(
    "checkedItems",
    []
  );

  const queries = [];
  const termsArray = terms
    .split("\n")
    .filter(t => t)
    .map(t => t.trim());
  const topicsArray = topics
    .split("\n")
    .filter(t => t)
    .map(t => t.trim());
  termsArray.forEach(term => {
    topicsArray.forEach(topic => {
      queries.push(`${term} ${topic}`);
    });
  });

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        <h1 className="text-4xl tracking-tight font-extrabold text-gray-900 sm:text-5xl md:text-6xl mb-4 mt-8">
          <span className="block xl:inline">Audience-finding</span>{" "}
          <span className="block text-blue-600 xl:inline">query generator</span>
          <span className="block xl:inline">
            {" "}
            for <a href="https://30x500.com/academy/">30x500</a>
          </span>
        </h1>
        <P>
          So you're going through{" "}
          <A href="https://30x500.com/academy/">30x500</A> and got to the part
          where you need to manually google all the terms to find your
          audience's watering holes. Ugh—how tedious!
        </P>
        <P>
          I know you're not supposed to automate the Sales Safari process but I
          couldn't help myself.
        </P>
        <P>
          Hi! I'm <A href="https://christian.gen.co">Christian Genco</A>, a{" "}
          <A href="https://30x500.com/academy/">30x500</A> alumni, and I built
          this thing to save you a little mental fatigue while going through
          this fantastic and difficult process of helping people.
        </P>
        <P>
          While this tool will help you come up with ideas of things to Google
          for, it still requires a lot of manual work to go through each of the
          generated links and notice patterns. I recommend not going any further
          in automation.
        </P>
        <P>Ready? Cool. Let's get started!</P>
        <P>
          First, type in a few of the ways that your audience describes
          themselves separated by newlines (the <Code>return</Code> key). Think
          of things like job titles, roles, tools and systems they use.
        </P>
        <Textarea
          className="bg-white shadow rounded-md w-full px-6 py-4 text-2xl font-mono"
          value={terms}
          onChange={e => setTerms(e.target.value)}
          placeholder={`Ex: Rails developer\nRuby on Rails developer\nRuby developer`}
        />
        <P>
          If my audience was Rails developers, I might start with the obvious{" "}
          <Code>Rails developer</Code>, the full title{" "}
          <Code>Ruby on Rails developer</Code>, and the related title{" "}
          <Code>Ruby developer</Code>. Get creative! You're looking for terms
          that people in your audience might use to describe themselves.
        </P>
        <P>
          <i>
            By the way, your work will be saved here if you leave and come back
            as long as you use the same computer and browser.
          </i>
        </P>
        {termsArray && termsArray.length > 0 && <Fragment>
        <P>
          Done with that? Cool. Next, glance at this list of watering hole
          keywords and add any that you think your audience might use to
          describe the places and ways they gather online. You probably don't
          need to—this list is pretty exhaustive.
        </P>
        <Textarea
          className="bg-white shadow rounded-md w-full px-6 py-4 text-2xl font-mono"
          value={topics}
          onChange={e => setTopics(e.target.value)}
          placeholder={`Ex: forum\nmailing list\npodcast`}
        />
        <P>
          <i>
            If you think of one that you think should be included by default on
            this list, <A href="https://twitter.com/cgenco">tweet me @cgenco</A>{" "}
            and I'll (probably) add it!
          </i>
        </P>
        <P>
          Alright now comes the fun part. Here's a list of all the google
          searches you can use to find your audience's watering holes:
        </P>

        {queries.map((query, i) => {
          return (
            <label
              key={query}
              className={`${
                i % 2 === 0 ? "bg-gray-50" : "bg-white"
              } block flex text-2xl items-center py-1 px-2 -mx-2 hover:bg-gray-200 rounded relative font-mono ${checkedItems.includes(query) ? 'line-through' : ''}`}
            >
              <input
                type="checkbox"
                className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded mr-2"
                checked={checkedItems.includes(query)}
                onChange={e => {
                  if(e.target.checked){
                    setCheckedItems([...checkedItems, query])
                  }else{
                    setCheckedItems(without(checkedItems, query))
                  }
                }}
              />
              {query}
              <a
                href={`https://google.com/search?q=${encodeURIComponent(query)}`}
                target="_blank"
                className="float-right text-blue-500 absolute right-0 pr-2"
              >
                <LinkIcon />
              </a>
            </label>
          );
        })}
        <P>
          <i>Oh wow look at how good it looks on mobile devices 😏</i>
        </P>
        <P>
          Have fun! If you've made it this far I'd love to meetcha on twitter{" "}
          <A href="https://twitter.com/cgenco">@cgenco</A>
        </P>
      </Fragment>}
      </div>
    </div>
  );
}

export default App;
